/**
 * `usePlayedVideo` hook plus video-control helpers for video iframes embedded
 * in a parent page that owns the player.
 *
 * The video element itself lives in the parent window; this hook subscribes to
 * `psi-video-state-update` and `psi-video-ended` `postMessage` events to mirror
 * the player state (currentTime, duration, paused, ended, ready) into React
 * state, and dispatches `psi-video-play`, `psi-video-pause`, and
 * `psi-video-seek` messages back up to control playback. On mount it requests
 * an immediate state sync via `psi-video-get-state` so newly mounted consumers
 * do not wait for the next tick. `getPrettyTimestamp` formats seconds as
 * `HH:MM:SS` (or `MM:SS` with `omitHours`).
 */

import { useEffect, useState } from 'react';

export function usePlayedVideo() {
    const [src, setSrc] = useState(null);
    const [time, setTime] = useState(0);
    const [duration, setDuration] = useState(0);
    const [paused, setPaused] = useState(true);
    const [ended, setEnded] = useState(false);
    const [ready, setReady] = useState(false);

    useEffect(() => {
        function handleMessage(event: MessageEvent) {
            if (event.data.type === 'psi-video-state-update') {
                setSrc(event.data.src);
                setTime(event.data.currentTime?.toFixed(1));
                setPaused(event.data.paused);
                setReady(event.data.ready);
                setEnded(event.data.ended);

                if (event.data.duration !== null && event.data.duration !== undefined && !isNaN(event.data.duration)) {
                    setDuration(event.data.duration);
                }
            }

            if (event.data.type === 'psi-video-ended') {
                setEnded(true);
            }
        }
        window.addEventListener('message', handleMessage);
        window.parent.postMessage({ type: 'psi-video-get-state' }, '*');

        return () => {
            window.removeEventListener('message', handleMessage);
        };
    }, []);
    return {
        currentTime: time,
        url: src,
        duration: duration,
        paused: paused,
        ended: ended,
        ready: ready,
        play: playVideo,
        pause: pauseVideo,
        seek: setVideoTime,
    };
}

function setVideoTime(videoTime: number) {
    window.parent.postMessage({ type: 'psi-video-seek', time: videoTime }, '*');
}

function pauseVideo() {
    window.parent.postMessage({ type: 'psi-video-pause' }, '*');
}

function playVideo() {
    window.parent.postMessage({ type: 'psi-video-play' }, '*');
}

/**
 * Returns a nicely formatted version of the given video time, e.g. 66.5 turns
 * into 00:01:06
 */
export function getPrettyTimestamp(videoTime: number | null | undefined, omitHours = false) {
    if (videoTime === null || videoTime === undefined) {
        return undefined;
    }

    const hours = Math.trunc(videoTime / 60 / 60)
        .toString()
        .padStart(2, '0');
    const minutes = Math.trunc(videoTime / 60)
        .toString()
        .padStart(2, '0');
    const seconds = Math.trunc(videoTime % 60)
        .toString()
        .padStart(2, '0');

    const prettyTimestamp = (omitHours && hours === '00' ? '' : hours + ':') + minutes + ':' + seconds;
    return prettyTimestamp;
}
